import { useCallback, useMemo } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import PATH from 'crono-fe-common/constants/path';
import type { Order } from 'crono-fe-common/types/userPreferences';
import { SequenceInstanceStatus } from 'crono-fe-common/types/enums/sequenceInstanceStatus';
import { StrategyOverviewTab } from './types';
import {
  STRATEGY_METRICS_FILTER_KEYS,
  STRATEGY_MY_CONTACTS_KEY,
  STRATEGY_PERSONALISE_FILTER_KEYS,
} from './navigationConstants';
import type {
  ContactsInStrategyFilterSortType,
  ContactsInStrategyOptionsForFilterBy,
} from './strategyPersonalise/contactsInStrategyFilter';
import { applySearchParamUpdate } from 'utils/browserUtils';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';

/**
 * What the toggle falls back to when the URL says nothing. Metrics always
 * starts on everyone's contacts. Personalising is work a manager does across
 * the team, so their Personalise tab starts wide too; a basic user cannot see
 * other users' sequences, so theirs stays on their own. Structure has no
 * toggle, hence no default — null, so an explicit choice passing through it
 * is written to the URL untouched instead of being normalised away.
 */
const defaultMyContacts = (
  tab: StrategyOverviewTab | null,
  isManager: boolean,
): boolean | null => {
  switch (tab) {
    case StrategyOverviewTab.Metrics:
      return false;
    case StrategyOverviewTab.Personalise:
      return !isManager;
    default:
      return null;
  }
};

/**
 * A basic user's Personalise tab is pinned to their own contacts no matter
 * what the URL claims — the API will not return other users' sequence
 * instances to them, and the toggle is not rendered.
 */
const resolveMyContacts = (
  tab: StrategyOverviewTab | null,
  isManager: boolean,
  explicit: boolean | null,
): boolean => {
  if (tab === StrategyOverviewTab.Personalise && !isManager) return true;
  return explicit ?? defaultMyContacts(tab, isManager) ?? true;
};

export type StrategyNavigationState = {
  id: number | null;
  tab: StrategyOverviewTab | null;

  /**
   * The "My contacts" toggle as the current tab sees it: the URL's explicit
   * choice when there is one, the tab's own default otherwise.
   */
  myContacts: boolean;

  personaliseFilters: {
    order: Order | null;
    orderBy: ContactsInStrategyFilterSortType | null;
    filterBy: ContactsInStrategyOptionsForFilterBy | null;
  };

  metricsFilters: {
    filterStatus: SequenceInstanceStatus | null;
  };
};

export type StrategyNavigationUpdate = Partial<StrategyNavigationState> & {
  id: number;
  tab: StrategyOverviewTab;
};

export type UseStrategyNavigationReturn = StrategyNavigationState & {
  /**
   * What the Personalise tab will show, resolved from any tab — for the
   * contact count on its tab badge, which is visible from all of them and
   * must not change as the user moves around.
   */
  personaliseMyContacts: boolean;
  navigateToStrategyDetail: (updates: StrategyNavigationUpdate) => void;
};

export const useStrategyNavigation = (): UseStrategyNavigationReturn => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { isManager } = useRoleCapabilities();

  const strategyNavigationValues = useMemo((): StrategyNavigationState & {
    personaliseMyContacts: boolean;
  } => {
    const strategyId = params.id ? parseInt(params.id) : null;

    const pathSegments = location.pathname.split('/');
    const sequenceIndex = pathSegments.indexOf(
      PATH.STRATEGIES.replace('/', ''),
    );
    const rawTab =
      sequenceIndex !== -1 ? pathSegments[sequenceIndex + 2] : null;
    const tab =
      rawTab &&
      Object.values(StrategyOverviewTab).includes(rawTab as StrategyOverviewTab)
        ? (rawTab as StrategyOverviewTab)
        : null;

    const orderParam = searchParams.get(STRATEGY_PERSONALISE_FILTER_KEYS.order);
    const orderByParam = searchParams.get(
      STRATEGY_PERSONALISE_FILTER_KEYS.orderBy,
    );
    const filterByParam = searchParams.get(
      STRATEGY_PERSONALISE_FILTER_KEYS.filterBy,
    );
    const myContactsParam = searchParams.get(STRATEGY_MY_CONTACTS_KEY);
    const filterStatusParam = searchParams.get(
      STRATEGY_METRICS_FILTER_KEYS.filterStatus,
    );

    const resolvedTab = tab ?? StrategyOverviewTab.Detail;
    const explicitMyContacts =
      myContactsParam === null ? null : myContactsParam !== 'false';

    return {
      id: strategyId,
      tab: resolvedTab,
      myContacts: resolveMyContacts(resolvedTab, isManager, explicitMyContacts),
      personaliseMyContacts: resolveMyContacts(
        StrategyOverviewTab.Personalise,
        isManager,
        explicitMyContacts,
      ),
      personaliseFilters: {
        order: (orderParam as Order) ?? 'desc',
        orderBy: (orderByParam as ContactsInStrategyFilterSortType) ?? 'date',
        filterBy: filterByParam as ContactsInStrategyOptionsForFilterBy | null,
      },
      metricsFilters: {
        filterStatus: filterStatusParam as SequenceInstanceStatus | null,
      },
    };
  }, [searchParams, params, location, isManager]);

  const navigateToStrategyDetail = useCallback(
    (updates: StrategyNavigationUpdate) => {
      const targetPath = generatePath(PATH.STRATEGY_DETAIL, {
        id: updates.id.toString(),
        tab: updates.tab === StrategyOverviewTab.Detail ? null : updates.tab,
      });

      const newSearchParams = new URLSearchParams();

      /**
       * What survives a tab-only navigation is the URL's explicit choice, not
       * the leaving tab's resolved value — carrying the latter turned one
       * tab's default into the next tab's explicit opt-in. A value matching
       * the target tab's default is dropped, keeping the usual URL clean.
       */
      const currentParam = searchParams.get(STRATEGY_MY_CONTACTS_KEY);
      const myContacts =
        updates.myContacts ??
        (currentParam === null ? null : currentParam !== 'false');
      const targetDefault = defaultMyContacts(updates.tab, isManager);

      applySearchParamUpdate(
        newSearchParams,
        STRATEGY_MY_CONTACTS_KEY,
        myContacts === null || myContacts === targetDefault
          ? null
          : String(myContacts),
      );

      // --- Personalise filters ---
      if (updates.personaliseFilters) {
        const { order, orderBy, filterBy } = updates.personaliseFilters;

        applySearchParamUpdate(
          newSearchParams,
          STRATEGY_PERSONALISE_FILTER_KEYS.order,
          order,
        );
        applySearchParamUpdate(
          newSearchParams,
          STRATEGY_PERSONALISE_FILTER_KEYS.orderBy,
          orderBy,
        );
        applySearchParamUpdate(
          newSearchParams,
          STRATEGY_PERSONALISE_FILTER_KEYS.filterBy,
          filterBy,
        );
      }

      // --- Metrics filters ---
      if (updates.metricsFilters) {
        const { filterStatus } = updates.metricsFilters;

        applySearchParamUpdate(
          newSearchParams,
          STRATEGY_METRICS_FILTER_KEYS.filterStatus,
          filterStatus,
        );
      }

      navigate({
        pathname: targetPath,
        search: newSearchParams.toString(),
      });
    },
    [searchParams, navigate, isManager],
  );

  return {
    ...strategyNavigationValues,
    navigateToStrategyDetail,
  };
};
